// Consultas › Documentos: la nota y su cadena Nota → Reserva → Despacho → DTE. Filtros por fecha,
// cliente, RUT, número interno, tipo (factura/boleta), folio tributario, estado y canal, todos en el
// servidor y paginados. Enter abre la nota (su detalle trae la línea de tiempo y el estado DTE).
// Buscar por FOLIO pregunta al DTE (su origen es la nota); lo demás, a Ventas.
import { useState } from 'react';
import { TAMANO_PAGINA, useDocumentosDte, useNotas, type FiltrosNotas } from '../api/consultas';
import {
  CANALES,
  DOCUMENTOS_SOLICITADOS,
  ESTADOS_NOTA,
  type ClienteEncontrado,
  type DocumentoDteListado,
  type NotaListada,
} from '../api/contratos';
import { EstadoNota, PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { fecha, fechaHora, NOMBRE_CANAL, pesos, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import { nombreTipoDte } from '../dte/EstadoDte';
import { DocumentosNota } from '../dte/DocumentosNota';

const COLUMNAS: Columna<NotaListada>[] = [
  {
    id: 'num',
    titulo: 'N° interno',
    celda: (n) =>
      n.venta_directa ? (
        <span title={`Registro interno ${n.numeroVisible}`}>
          Venta directa · {n.documento_solicitado === 'BOLETA' ? 'boleta' : 'factura'}
        </span>
      ) : (
        <span className="mono">{n.numeroVisible}</span>
      ),
    ancho: '8rem',
  },
  { id: 'fecha', titulo: 'Fecha', celda: (n) => fecha(n.fecha_negocio), ancho: '7rem' },
  { id: 'cliente', titulo: 'Cliente', celda: (n) => n.cliente_razon_social },
  {
    id: 'rut',
    titulo: 'RUT',
    celda: (n) => <span className="mono">{n.cliente_rut ? rut(n.cliente_rut) : '—'}</span>,
    ancho: '9rem',
  },
  { id: 'canal', titulo: 'Canal', celda: (n) => NOMBRE_CANAL[n.canal] ?? n.canal, ancho: '7rem' },
  {
    id: 'doc',
    titulo: 'Documento',
    celda: (n) => (
      <DocumentosNota
        documentos={n.documentos}
        vacio={`${n.documento_solicitado.toLowerCase()} por emitir`}
      />
    ),
    ancho: '11rem',
  },
  { id: 'estado', titulo: 'Estado', celda: (n) => <EstadoNota estado={n.estado} />, ancho: '8rem' },
  {
    id: 'conf',
    titulo: 'Confirmada',
    celda: (n) => (n.confirmada_en ? fechaHora(n.confirmada_en) : '—'),
    ancho: '9rem',
  },
];

const COLUMNAS_DTE: Columna<DocumentoDteListado>[] = [
  { id: 'tipo', titulo: 'Tipo', celda: (d) => nombreTipoDte(d.tipo_dte) },
  { id: 'folio', titulo: 'Folio', celda: (d) => <span className="mono">{d.folio ?? '—'}</span> },
  { id: 'estado', titulo: 'Documento', celda: (d) => d.estado },
  { id: 'envio', titulo: 'Envío', celda: (d) => d.estado_envio },
  { id: 'sii', titulo: 'SII', celda: (d) => d.estado_sii },
  { id: 'monto', titulo: 'Monto', celda: (d) => pesos(d.monto_total), alinear: 'der' },
  {
    id: 'fecha',
    titulo: 'Emisión',
    celda: (d) => (d.fecha_emision ? fecha(d.fecha_emision) : '—'),
  },
];

export function ConsultaDocumentos({
  alAbrirNota,
  alAbrirDte,
  estadoInicial,
}: {
  alAbrirNota: (id: string, numero: string) => void;
  /** Buscar por folio abre el DOCUMENTO, no la nota (2026-10-26). */
  alAbrirDte?: (documentoId: string, titulo: string) => void;
  /** Filtro de estado con que abre (p. ej. «Pendientes de firma» = EN_APROBACION). */
  estadoInicial?: string | undefined;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const [f, setF] = useState<FiltrosNotas>({
    pagina: 1,
    ...(estadoInicial ? { estado: estadoInicial } : {}),
  });
  const [folio, setFolio] = useState('');
  const [tipoDte, setTipoDte] = useState('');
  const [cliente, setCliente] = useState<ClienteEncontrado | null>(null);
  const [buscando, setBuscando] = useState(false);
  const porFolio = !!folio;
  // «Pendientes de firma» se refresca sola: el supervisor ve llegar las solicitudes.
  const q = useNotas(empresaId, f, !porFolio, f.estado === 'EN_APROBACION' ? 10_000 : false);
  const dte = useDocumentosDte(empresaId, { folio, tipoDte: tipoDte || undefined }, porFolio);
  const cambiar = (c: Partial<FiltrosNotas>) => setF((x) => ({ ...x, ...c, pagina: 1 }));
  return (
    <div className="pantalla consulta" data-pantalla="documentos">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Desde</span>
          <input
            type="date"
            aria-label="Desde"
            value={f.desde ?? ''}
            onChange={(e) => cambiar({ desde: e.target.value || undefined })}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Hasta</span>
          <input
            type="date"
            aria-label="Hasta"
            value={f.hasta ?? ''}
            onChange={(e) => cambiar({ hasta: e.target.value || undefined })}
          />
        </label>
        <label className="campo campo-corto">
          <span className="etiqueta">N° interno</span>
          <input
            inputMode="numeric"
            aria-label="Número interno"
            value={f.numero ?? ''}
            onChange={(e) => cambiar({ numero: e.target.value.replace(/\D/g, '') || undefined })}
          />
        </label>
        <label className="campo campo-corto">
          <span className="etiqueta">RUT cliente</span>
          <input
            aria-label="RUT del cliente"
            placeholder="76111222"
            value={f.rut ?? ''}
            onChange={(e) =>
              cambiar({
                rut: e.target.value.replace(/[^\dkK]/g, '').replace(/[kK]$/, '') || undefined,
              })
            }
          />
        </label>
        <div className="campo">
          <span className="etiqueta">Cliente</span>
          <div className="fila-datos">
            <button type="button" className="selector-cliente" onClick={() => setBuscando(true)}>
              {cliente ? cliente.razon_social : 'Todos (buscar…)'}
            </button>
            {cliente && (
              <button
                type="button"
                className="boton-mini"
                aria-label="Quitar cliente"
                onClick={() => {
                  setCliente(null);
                  cambiar({ clienteEntidadId: undefined });
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <label className="campo">
          <span className="etiqueta">Tipo</span>
          <select
            aria-label="Tipo de documento"
            value={f.documento ?? ''}
            onChange={(e) => cambiar({ documento: e.target.value || undefined })}
          >
            <option value="">Todos</option>
            {DOCUMENTOS_SOLICITADOS.map((d) => (
              <option key={d} value={d}>
                {d === 'FACTURA' ? 'Factura' : 'Boleta'}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado"
            value={f.estado ?? ''}
            onChange={(e) => cambiar({ estado: e.target.value || undefined })}
          >
            <option value="">Todos</option>
            {ESTADOS_NOTA.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Canal</span>
          <select
            aria-label="Canal"
            value={f.canal ?? ''}
            onChange={(e) => cambiar({ canal: e.target.value || undefined })}
          >
            <option value="">Todos</option>
            {CANALES.map((c) => (
              <option key={c} value={c}>
                {NOMBRE_CANAL[c]}
              </option>
            ))}
          </select>
        </label>
        {puede('dte.ver') && (
          <>
            <label className="campo campo-corto">
              <span className="etiqueta">Folio DTE</span>
              <input
                inputMode="numeric"
                aria-label="Folio tributario"
                value={folio}
                onChange={(e) => setFolio(e.target.value.replace(/\D/g, ''))}
              />
            </label>
            <label className="campo">
              <span className="etiqueta">Tipo DTE</span>
              <select
                aria-label="Tipo DTE"
                value={tipoDte}
                onChange={(e) => setTipoDte(e.target.value)}
              >
                <option value="">Todos</option>
                {[33, 34, 39, 41, 52, 56, 61].map((t) => (
                  <option key={t} value={t}>
                    {t} · {nombreTipoDte(t)}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
      </div>
      {porFolio ? (
        <>
          {dte.isError && <PanelError error={dte.error} />}
          <Grilla
            etiqueta="Documentos tributarios"
            columnas={COLUMNAS_DTE}
            filas={dte.data ?? []}
            clave={(d) => d.id}
            cargando={dte.isFetching}
            vacio="Sin documentos tributarios con ese folio en esta empresa"
            alAbrir={(d) =>
              alAbrirDte
                ? alAbrirDte(d.id, `${nombreTipoDte(d.tipo_dte)} N° ${d.folio ?? ''}`)
                : alAbrirNota(d.origen_id, `Nota de ${nombreTipoDte(d.tipo_dte)} ${d.folio ?? ''}`)
            }
          />
          <p className="ayuda">Enter abre el documento.</p>
        </>
      ) : (
        <>
          {q.isError && <PanelError error={q.error} />}
          <Grilla
            etiqueta="Documentos"
            columnas={COLUMNAS}
            filas={q.data?.items ?? []}
            clave={(n) => n.id}
            cargando={q.isFetching}
            alAbrir={(n) => alAbrirNota(n.id, n.numeroVisible)}
            paginacion={{
              pagina: f.pagina,
              hayMas: (q.data?.items.length ?? 0) === TAMANO_PAGINA,
              alCambiar: (pagina) => setF((x) => ({ ...x, pagina })),
            }}
          />
          <p className="ayuda">
            Enter abre la nota: su detalle muestra la línea de tiempo (creada, confirmada,
            reservada, despachada, DTE) y el estado tributario.
          </p>
        </>
      )}
      {buscando && (
        <BuscadorCliente
          empresaId={empresaId}
          alCerrar={() => setBuscando(false)}
          alElegir={(c) => {
            setCliente(c);
            cambiar({ clienteEntidadId: c.id });
            setBuscando(false);
          }}
        />
      )}
    </div>
  );
}
